import React, { useMemo, useState } from 'react';
import { Bot, ChevronDown, ChevronRight, Cog, PhoneCall, UserCheck } from 'lucide-react';
import { InfoTip } from '@/pages/reports/_Components/InfoTip';
import BucketChip from '../components/BucketChip';
import NumLink from '../components/NumLink';
import HourStatusRow from '../components/HourStatusRow';
import RateBar from '../components/RateBar';
import { EMPTY, PANEL, SECTION_TITLE, TD, TH } from '../components/tableStyles';
import { useReport } from '../ReportContext';
import { adaptHourlyWork } from '../adapt';
import { num } from '../format';
import { hourRange, useHourly } from '../hourly';
import { totals } from '../stats';
import { BUCKETS, WORK_BUCKETS } from '../theme';
import type { OpenDrill, WorkRow, WorkView } from '../types';

interface Props { rows: WorkRow[]; openDrill: OpenDrill }

const VIEWS: { key: WorkView; label: string; hint: string }[] = [
  { key: 'type', label: 'By work type', hint: 'One row per bucket. Every number is in work items.' },
  { key: 'agent', label: 'By agent', hint: 'Every number is in work items.' },
  { key: 'status', label: 'By status', hint: 'Open, closed by an agent, or closed by the system.' },
];

const dash = <span className="text-slate-500">—</span>;
const Same: React.FC<{ n: number; of: number; ofLabel: string }> = ({ n, of, ofLabel }) =>
  n === of ? <span className="text-xs text-slate-600" title={`Same as ${ofLabel}`}>= {ofLabel}</span> : <span className="tabular-nums">{num(n)}</span>;

const WorkTab: React.FC<Props> = ({ rows, openDrill }) => {
  const { automation: AUTOMATION } = useReport();
  const [view, setView] = useState<WorkView>('type');
  const perBucket = useMemo(() => WORK_BUCKETS.map(b => ({ b, t: totals(rows.filter(r => r.bucket === b)) })).filter(x => x.t.received > 0), [rows]);
  const all = useMemo(() => totals(rows), [rows]);
  const unsplit = useMemo(() => rows.reduce((a, r) => a + r.workedUnsplit, 0), [rows]);
  const abc = perBucket.find(x => x.b === 'abc')?.t;
  const reorder = perBucket.find(x => x.b === 'reorder')?.t;
  const agents = useMemo(() => Array.from(new Set(rows.map(r => r.agent))).sort((a, b) => (a === 'Unassigned' ? 1 : b === 'Unassigned' ? -1 : a.localeCompare(b))), [rows]);

  // Hour-of-day rows under each bucket, fetched the first time one is opened.
  const hourly = useHourly('work', adaptHourlyWork);
  const [open, setOpen] = useState<Set<string>>(new Set());
  const toggle = (b: string) => { setOpen(prev => { const n = new Set(prev); if (n.has(b)) n.delete(b); else n.add(b); return n; }); hourly.load(); };
  const expandAll = () => { setOpen(new Set(perBucket.map(x => x.b))); hourly.load(); };

  if (perBucket.length === 0) return <div className={EMPTY}>No work items for this selection.</div>;

  const drill = (title: string, bucket: typeof WORK_BUCKETS[number] | 'all', agent: string, total: number, worked?: 'worked' | 'unworked'): (() => void) =>
    () => openDrill({ title, subtitle: bucket === 'all' ? undefined : BUCKETS[bucket].unit, kind: 'work', agent, bucket, total, worked });

  return (
    <div className="space-y-6">
      <div className={PANEL}>
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-4 py-3 dark:border-slate-700">
          <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Work views">
            {VIEWS.map(v => (
              <button key={v.key} type="button" role="tab" aria-selected={view === v.key} onClick={() => setView(v.key)}
                className={`rounded-full px-3 py-1 text-sm ${view === v.key
                  ? 'bg-violet-100 text-violet-800 dark:bg-violet-400/20 dark:text-violet-100'
                  : 'text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-700'}`}>{v.label}</button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            {view === 'type' && hourly.available && (
              <div className="flex gap-2 text-sm">
                <button type="button" onClick={expandAll} className="rounded-lg px-2.5 py-1 text-violet-700 hover:bg-violet-50 dark:text-violet-300 dark:hover:bg-violet-400/10">Expand all hours</button>
                <button type="button" onClick={() => setOpen(new Set())} className="rounded-lg px-2.5 py-1 text-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700">Collapse all</button>
              </div>
            )}
            <p className="text-xs text-slate-600">{VIEWS.find(v => v.key === view)?.hint}</p>
          </div>
        </div>

        <div className="overflow-x-auto">
          {view === 'type' && (
            <table className="min-w-full">
              <thead className="bg-slate-50/80 dark:bg-slate-800"><tr>
                <th className={`${TH} text-left`}>Work type</th>
                <th className={`${TH} text-right`}>Work items<InfoTip text="One unit of work. ABC: one per cart event; one disposition closes every earlier open cart of that customer. Reorder: one per product due date. EDD, NDR, RTO: one per order issue. Agent and Phone leads: one per lead." /></th>
                <th className={`${TH} text-right`}>Worked</th>
                <th className={`${TH} text-right`}>Unworked</th>
                <th className={`${TH} text-right`}>Worked %</th>
                <th className={`${TH} text-right`}>Unique customers<InfoTip text="Distinct people. Fewer than work items when one customer has several (for example several abandoned carts)." /></th>
                <th className={`${TH} text-right`}>Unique orders<InfoTip text="EDD, NDR and RTO only: distinct order IDs." /></th>
              </tr></thead>
              <tbody className="divide-y divide-slate-50 dark:divide-slate-700/60">
                {perBucket.map(({ b, t }) => {
                  const unit = 'items';
                  const isOpen = open.has(b);
                  const hours = hourly.data?.[b] ?? [];
                  return (
                    <React.Fragment key={b}>
                    <tr className={BUCKETS[b].tint}>
                      <td className={TD}>
                        {hourly.available ? (
                          <button type="button" onClick={() => toggle(b)} aria-expanded={isOpen} title="Hourly breakdown" className="flex items-center gap-1.5">
                            {isOpen ? <ChevronDown className="h-4 w-4 shrink-0 text-slate-600" /> : <ChevronRight className="h-4 w-4 shrink-0 text-slate-600" />}
                            <BucketChip bucket={b} />
                          </button>
                        ) : <BucketChip bucket={b} />}
                      </td>
                      <td className={`${TD} text-right`}><NumLink text={num(t.workItems)} onClick={drill(`${BUCKETS[b].label} — work items`, b, 'all', t.workItems)} /></td>
                      <td className={`${TD} text-right`}>
                        <NumLink text={num(t.worked)} zero={t.worked === 0} onClick={drill(`${BUCKETS[b].label} — worked ${unit}`, b, 'all', t.worked, 'worked')} />
                        <span className="ml-1 text-[11px] text-slate-600">{unit}</span>
                      </td>
                      <td className={`${TD} text-right`}>
                        <NumLink text={num(t.unworked)} zero={t.unworked === 0} onClick={drill(`${BUCKETS[b].label} — unworked ${unit}`, b, 'all', t.unworked, 'unworked')} />
                        <span className="ml-1 text-[11px] text-slate-600">{unit}</span>
                      </td>
                      <td className={TD}><RateBar value={t.workRate} /></td>
                      <td className={`${TD} text-right`}><Same n={t.uniqueCustomers} of={t.workItems} ofLabel="items" /></td>
                      <td className={`${TD} text-right`}>{b === 'edd' || b === 'ndr' || b === 'rto' ? <Same n={t.uniqueOrders} of={t.workItems} ofLabel="items" /> : dash}</td>
                    </tr>
                    {isOpen && <HourStatusRow state={hourly} colSpan={7} hasRows={hours.length > 0} />}
                    {isOpen && hours.map(({ hour, row: r }) => {
                      const ht = totals([r]);
                      const label = `${BUCKETS[b].label} · ${hourRange(hour)}`;
                      const hd = (title: string, total: number, worked?: 'worked' | 'unworked') => () =>
                        openDrill({ title, subtitle: b === 'reorder' ? 'hour delivered' : BUCKETS[b].unit, kind: 'work', agent: 'all', bucket: b, total, worked, hour });
                      return (
                        <tr key={`${b}-${hour}`} className={`border-t border-slate-50 dark:border-slate-700/50 ${BUCKETS[b].tint}`}>
                          <td className={`${TD} whitespace-nowrap pl-9`}>{hourRange(hour)}{b === 'reorder' && <span className="ml-1.5 text-[11px] text-slate-600">delivered</span>}</td>
                          <td className={`${TD} text-right`}><NumLink text={num(ht.workItems)} zero={ht.workItems === 0} onClick={hd(`${label} — work items`, ht.workItems)} /></td>
                          <td className={`${TD} text-right`}><NumLink text={num(ht.worked)} zero={ht.worked === 0} onClick={hd(`${label} — worked`, ht.worked, 'worked')} /></td>
                          <td className={`${TD} text-right`}><NumLink text={num(ht.unworked)} zero={ht.unworked === 0} onClick={hd(`${label} — unworked`, ht.unworked, 'unworked')} /></td>
                          <td className={TD}><RateBar value={ht.workRate} /></td>
                          <td className={`${TD} text-right tabular-nums`}>{num(ht.uniqueCustomers)}</td>
                          <td className={`${TD} text-right`}>{b === 'edd' || b === 'ndr' || b === 'rto' ? <span className="tabular-nums">{num(ht.uniqueOrders)}</span> : dash}</td>
                        </tr>
                      );
                    })}
                    {isOpen && b === 'reorder' && hours.length > 0 && (
                      <tr className="border-t border-slate-50 dark:border-slate-700/50">
                        <td colSpan={7} className={`${TD} pl-9 text-xs text-slate-600`}>Reorder is due on a date, not at a time. Hours show when the order was delivered; the time filter does not apply to Reorder.</td>
                      </tr>
                    )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}

          {view === 'agent' && (
            <table className="min-w-full">
              <thead className="bg-slate-50/80 dark:bg-slate-800">
                <tr>
                  <th className={`${TH} text-left`}>Agent</th>
                  <th className={`${TH} text-right`}>Work items</th><th className={`${TH} text-right`}>Worked</th><th className={`${TH} text-right`}>Unworked</th>
                  <th className={`${TH} text-right`}>Worked %</th><th className={`${TH} text-right`}>Unique customers</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50 dark:divide-slate-700/60">
                {agents.map(a => {
                  const t = totals(rows.filter(r => r.agent === a));
                  return (
                    <tr key={a}>
                      <td className={`${TD} font-medium`}>{a}</td>
                      <td className={`${TD} text-right`}><NumLink text={num(t.received)} zero={t.received === 0} onClick={drill(`${a} — work items`, 'all', a, t.received)} /></td>
                      <td className={`${TD} text-right tabular-nums`}>{num(t.worked)}</td>
                      <td className={`${TD} text-right tabular-nums`}>{num(t.unworked)}</td>
                      <td className={TD}><RateBar value={t.workRate} color="#fdc9a0" /></td>
                      <td className={`${TD} text-right tabular-nums`}>{num(t.uniqueCustomers)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {view === 'status' && (
            <table className="min-w-full">
              <thead className="bg-slate-50/80 dark:bg-slate-800"><tr>
                <th className={`${TH} text-left`}>Work type</th>
                <th className={`${TH} text-right`}>Received</th>
                <th className={`${TH} text-right`}>Open</th>
                <th className={`${TH} text-right`}>Closed by agent</th>
                <th className={`${TH} text-right`}>Closed by system<InfoTip text="Closed with no agent action: delivered, returned, aged out, auto-updated. Not counted as worked." /></th>
              </tr></thead>
              <tbody className="divide-y divide-slate-50 dark:divide-slate-700/60">
                {perBucket.map(({ b, t }) => (
                  <tr key={b} className={BUCKETS[b].tint}>
                    <td className={TD}><BucketChip bucket={b} /></td>
                    <td className={`${TD} text-right tabular-nums`}>{num(t.received)}</td>
                    <td className={`${TD} text-right tabular-nums`}>{num(t.open)}</td>
                    <td className={`${TD} text-right tabular-nums`}>{num(t.closedByAgent)}</td>
                    <td className={`${TD} text-right tabular-nums text-slate-700`}>{num(t.closedBySystem)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className={`${PANEL} p-4`}>
          <h3 className={`${SECTION_TITLE} mb-1`}>How ABC is counted</h3>
          <p className="mb-3 text-xs text-slate-700 dark:text-slate-300">Each cart a customer leaves is one work item. One disposition closes every earlier open cart of that customer; a later cart opens a new work item.</p>
          {abc ? (
            <dl className="grid grid-cols-2 gap-2 text-sm">
              <div className="rounded-xl bg-violet-50 p-3 dark:bg-violet-400/10"><dt className="text-xs text-slate-700">Work items (carts)</dt><dd className="text-xl font-bold text-slate-800 dark:text-white">{num(abc.workItems)}</dd></div>
              <div className="rounded-xl bg-violet-50 p-3 dark:bg-violet-400/10"><dt className="text-xs text-slate-700">Unique customers</dt><dd className="text-xl font-bold text-slate-800 dark:text-white">{num(abc.uniqueCustomers)}</dd></div>
              <div className="rounded-xl bg-emerald-50 p-3 dark:bg-emerald-400/10"><dt className="text-xs text-slate-700">Worked</dt><dd className="text-xl font-bold text-slate-800 dark:text-white">{num(abc.worked)}</dd></div>
              <div className="rounded-xl bg-amber-50 p-3 dark:bg-amber-400/10"><dt className="text-xs text-slate-700">Unworked</dt><dd className="text-xl font-bold text-slate-800 dark:text-white">{num(abc.unworked)}</dd></div>
            </dl>
          ) : <p className="text-sm text-slate-600">No ABC work in this selection.</p>}
        </div>

        <div className={`${PANEL} p-4`}>
          <h3 className={`${SECTION_TITLE} mb-1`}>How Reorder is counted</h3>
          <p className="mb-3 text-xs text-slate-700 dark:text-slate-300">Each reorder row with its own product and due date is one work item. One customer can hold several, so the two numbers differ.</p>
          {reorder ? (
            <dl className="grid grid-cols-2 gap-2 text-sm">
              <div className="rounded-xl bg-orange-50 p-3 dark:bg-orange-400/10"><dt className="text-xs text-slate-700">Reorder work items</dt><dd className="text-xl font-bold text-slate-800 dark:text-white">{num(reorder.workItems)}</dd></div>
              <div className="rounded-xl bg-orange-50 p-3 dark:bg-orange-400/10"><dt className="text-xs text-slate-700">Unique customers</dt><dd className="text-xl font-bold text-slate-800 dark:text-white">{num(reorder.uniqueCustomers)}</dd></div>
              <div className="rounded-xl bg-emerald-50 p-3 dark:bg-emerald-400/10"><dt className="text-xs text-slate-700">Worked</dt><dd className="text-xl font-bold text-slate-800 dark:text-white">{num(reorder.worked)}</dd></div>
              <div className="rounded-xl bg-amber-50 p-3 dark:bg-amber-400/10"><dt className="text-xs text-slate-700">Unworked</dt><dd className="text-xl font-bold text-slate-800 dark:text-white">{num(reorder.unworked)}</dd></div>
            </dl>
          ) : <p className="text-sm text-slate-600">No reorder work in this selection.</p>}
        </div>

        <div className={`${PANEL} p-4`}>
          <h3 className={`${SECTION_TITLE} mb-1`}>Worked, by who did it</h3>
          <p className="mb-3 text-xs text-slate-700 dark:text-slate-300">A human taking the action counts as worked, even when the disposition arrives through the dialer.</p>
          <ul className="space-y-2 text-sm">
            <li className="flex items-center justify-between rounded-xl bg-emerald-50 px-3 py-2 dark:bg-emerald-400/10">
              <span className="flex items-center gap-2"><UserCheck className="h-4 w-4 text-emerald-600" /> Manual (on screen) <span className="text-[11px] text-slate-600">MANUAL</span></span>
              <b className="tabular-nums">{num(all.workedManual)}</b></li>
            <li className="flex items-center justify-between rounded-xl bg-sky-50 px-3 py-2 dark:bg-sky-400/10">
              <span className="flex items-center gap-2"><PhoneCall className="h-4 w-4 text-sky-600" /> Tata Tele call by an agent <span className="text-[11px] text-slate-600">TATA_TELE</span></span>
              <b className="tabular-nums">{num(all.workedTata)}</b></li>
            {unsplit > 0 && (
              <li className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2 dark:bg-slate-400/10" title="A person updated the disposition, but it was written before the source was recorded: Manual vs Tata Tele is not known.">
                <span className="flex items-center gap-2"><UserCheck className="h-4 w-4 text-slate-700" /> Before source was recorded <span className="text-[11px] text-slate-600">unsplit</span></span>
                <b className="tabular-nums">{num(unsplit)}</b></li>
            )}
          </ul>
          <h4 className="mb-1 mt-4 text-xs font-semibold uppercase tracking-wide text-slate-600">Automation — not worked</h4>
          <ul className="space-y-2 text-sm">
            <li className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2 dark:bg-slate-400/10">
              <span className="flex items-center gap-2"><Bot className="h-4 w-4 text-slate-700" /> Engati AI caller <span className="text-[11px] text-slate-600">ENGATI_AI</span></span>
              <b className="tabular-nums">{num(AUTOMATION.engatiAi)}</b></li>
            <li className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2 dark:bg-slate-400/10">
              <span className="flex items-center gap-2"><Cog className="h-4 w-4 text-slate-700" /> ABC Auto Updated <span className="text-[11px] text-slate-600">SYSTEM</span></span>
              <b className="tabular-nums">{num(AUTOMATION.abcAutoUpdated)}</b></li>
            <li className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2 dark:bg-slate-400/10">
              <span className="flex items-center gap-2"><Cog className="h-4 w-4 text-slate-700" /> Other system closes <span className="text-[11px] text-slate-600">SYSTEM</span></span>
              <b className="tabular-nums">{num(AUTOMATION.otherSystem)}</b></li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default WorkTab;
